import React from "react";
import { LogoMarquee } from "../../_components/LogoMarquee";
import { ProjectOutsource } from "../../_components/services-tabs";
import { FooterV2 } from "../../_components/FooterV2";
import { AnimatedCounter } from "../../_components/AnimatedCounter";
import heroImage from "../../assets/homeowners-planning.jpeg";
import transparentPricing from "../../assets/transparent-pricing-card.png";
import LHGuarantee from "../../assets/lh-guarantee-card.png";
import builtInWorkerInsurance from "../../assets/worker-insurance-card.png";
import { logEvent } from "helpers/analytics";
import moneyIconCircle from "../../assets/money-icon-circle.png";
import smileyFaceIconCircle from "../../assets/smiley-face-icon-circle.png";
import { HeroSectionCTAV2 } from "features/homeowners/hero-section-cta-v2";
import { Helmet } from "react-helmet";
import {
  Link as RouterLink,
  Switch,
  useHistory,
  useRouteMatch,
} from "react-router-dom";
import { Route } from "react-router-dom";
import { Root } from "features/homeowners/hire/root";
import { useCategories } from "hooks/useCategories";
import { FaArrowRight } from "react-icons/fa";
import { PageSection } from "../../_components/PageSection";
import Marquee from "react-fast-marquee";
import HandshakeIcon from "../../assets/homeowners/icons/handshake.svg";
import NairaNoteIcon from "../../assets/homeowners/icons/naira-note.svg";
import WorkerInsuranceIcon from "../../assets/homeowners/icons/worker-insurance.svg";
import ChecklistIcon from "../../assets/homeowners/icons/checklist.svg";
import EvidenceIcon from "../../assets/homeowners/icons/evidence.svg";
import SafePaymentsIcon from "../../assets/homeowners/icons/safe-payments.svg";
import { ProjectOutsourcingModal } from "../../features/project-outsourcing";
import ClientsFAQs from "_components/clients-landing-pages/ClientsFAQs";
import { homeownerFAQs } from "../faqs/constants";

const stats = [
  {
    title: "Over 500 tasks completed",
    icon: moneyIconCircle,
  },
  {
    title: "99.9% first time fix rate",
    icon: smileyFaceIconCircle,
  },
  {
    title: "95% job satisfaction from completed projects",
    icon: smileyFaceIconCircle,
  },
];

const HomeOwnersV2: React.FC = () => {
  const [isProjectOutsourcingOpen, setProjectOutsourcingOpen] =
    React.useState(false);
  const [projectServiceType, setProjectServiceType] =
    React.useState<"construction" | "facility-maintenance" | "renovations">(
      "construction"
    );

  const handleCtaClick = () => {
    logEvent("homeowners_landing_page_cta_click", {
      type: "homeowner_account_creation",
    });
    window.open(`${process.env.REACT_APP_HOMEOWNER_DASHBOARD_URL}`, "_blank");
  };

  const handleStartProjectFromService = (
    service: "construction" | "facility-maintenance" | "renovations"
  ) => {
    logEvent("homeowners_landing_page_cta_click", {
      type: "project_outsourcing",
    });
    setProjectServiceType(service);
    setProjectOutsourcingOpen(true);
  };

  const match = useRouteMatch();
  const { categories, loading: categoriesLoading } = useCategories();

  const prioritySortedNonOtherCategories = React.useMemo(() => {
    return categories
      ?.slice()
      .sort((a, b) => b.priority - a.priority)
      .filter((category) => category.name !== "Other");
  }, [categories]);

  const otherCategory = React.useMemo(
    () => categories?.find((category) => category.name === "Other"),
    [categories]
  );

  const navigate = useHistory().push;

  return (
    <div>
      <Helmet>
        <title>
          LaborHack Homeowners - Hire certified artisans for your home needs
        </title>
        <meta
          name="description"
          content="Don't let home tasks take over your precious time. Hire well-vetted workers and artisans to handle all your electrical, plumbing, cleaning, fumigation and AC needs."
        />
      </Helmet>
      <ProjectOutsourcingModal
        isOpen={isProjectOutsourcingOpen}
        onClose={() => setProjectOutsourcingOpen(false)}
        userType="homeowner"
        serviceType={projectServiceType}
      />
      {/* Hero Section */}
      <div className="relative w-full h-[500px] md:h-[632px] bg-[#F7F4F4]">
        {/* Background Image with Overlay */}
        <div
          className="absolute top-0 left-0 w-full h-full bg-cover bg-center"
          style={{
            backgroundImage: `url(${heroImage})`,
          }}
        />

        {/* Dark overlay on the left side */}
        <div
          className="absolute top-0 left-0 w-full md:w-[50vw] h-full"
          style={{ backgroundColor: "rgba(18, 18, 18, 0.3)" }}
        />

        {/* Additional overlay for text readability */}
        <div
          className="absolute top-0 left-0 w-full h-full"
          style={{ backgroundColor: "rgba(49, 28, 28, 0.4)" }}
        />

        {/* Content */}
        <PageSection className="h-full relative z-10">
          <div className="h-full flex items-center">
            <div className="max-w-full md:max-w-[680px]">
              <div className="flex flex-col gap-5 md:gap-8">
                {/* Title and Description */}
                <div className="flex flex-col gap-3 md:gap-4">
                  <h1 className="text-[#F0F0F0] text-[36px] md:text-[56px] font-medium leading-tight md:leading-[1.1428571428571428] tracking-tight">
                    Hire quality, <br className="hidden md:block" /> skilled
                    workers for <br className="hidden md:block" /> your
                    projects.
                  </h1>
                </div>
                <HeroSectionCTAV2 />
              </div>
            </div>
          </div>
        </PageSection>
      </div>

      {/* Hire Section */}
      <Switch>
        <Route path={`${match.path}/hire/:requestId`}>
          <Root />
        </Route>
      </Switch>

      <div className="flex w-full justify-center bg-white py-12">
        <div className="w-full max-w-screen-xl justify-center items-center flex flex-col gap-4">
          <h2 className="text-xl font-medium text-prussianBlue-300  ">
            Trusted By
          </h2>

          <AnimatedCounter
            target={1000}
            suffix="+ Homeowners"
            className="text-3xl md:text-5xl font-semibold font-aeonik text-prussianBlue-500"
          />
        </div>
      </div>

      {/* Third Section - Professional Access */}
      <PageSection className=" border-y border-prussianBlue-300 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
          {/* Transparent pricing */}
          <div className="flex flex-col">
            <div className="bg-[#EDEDED] h-fit mb-5 border border-prussianBlue-300 flex px-5 pt-5">
              <div className="bg-white border border-b-0 border-prussianBlue-300 w-full flex items-center justify-center p-12">
                <img src={NairaNoteIcon} alt="Transparent pricing" />
              </div>
            </div>
            <h3 className="lg:w-[60%] text-lg lg:text-xl font-normal text-gray-900">
              Transparent pricing
            </h3>
          </div>

          {/* LaborHack Guarantee */}
          <div className="flex flex-col">
            <div className="bg-[#EDEDED] h-fit mb-5 border border-prussianBlue-300 flex px-5 pt-5">
              <div className="bg-white border border-b-0 border-prussianBlue-300 w-full flex items-center justify-center p-12">
                <img src={HandshakeIcon} alt="Transparent pricing" />
              </div>
            </div>
            <h3 className="lg:w-[60%] text-lg lg:text-xl font-normal text-gray-900">
              Enjoy the LaborHack Guarantee
            </h3>
          </div>

          {/* Built-in worker insurance */}
          <div className="flex flex-col">
            <div className="bg-[#EDEDED] h-fit mb-5 border border-prussianBlue-300 flex px-5 pt-5">
              <div className="bg-white border border-b-0 border-prussianBlue-300 w-full flex items-center justify-center p-12">
                <img
                  src={WorkerInsuranceIcon}
                  alt="Built-in worker insurance"
                />
              </div>
            </div>
            <h3 className="lg:w-[60%] text-lg lg:text-xl font-normal text-gray-900">
              Built-in worker insurance
            </h3>
          </div>
        </div>
      </PageSection>

      {/* Fourth Section - Find talent by category */}
      <PageSection className="bg-[#FEFEFE] py-12 md:py-16">
        <div className="mb-6 md:mb-8">
          <h2 className="text-2xl md:text-[32px] font-medium text-[#212121] leading-[1.25] font-[AeonikPro]">
            Find talent by category
          </h2>
        </div>

        {/* Categories Grid */}
        {categoriesLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 lg:gap-8">
            {new Array(15).fill(0).map((_, idx) => (
              <div
                key={idx}
                className="w-full h-[65px] sm:h-[75px] lg:h-[85px] bg-gray-200 animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 lg:gap-8">
            {prioritySortedNonOtherCategories?.map((category) => (
              <RouterLink
                key={category.id}
                to={`${match.path}/hire/new?category=${category.id}`}
                onClick={() => {
                  logEvent("hero_section_cta_category_clicked", {
                    category: category.name,
                  });
                  logEvent("homeowner_hire_workflow_started", {
                    category: category.name,
                  });
                }}
                className="group"
              >
                <div className="w-full h-[65px] sm:h-[75px] lg:h-[85px] bg-[#E8E7E7] px-4 py-4 sm:px-6 sm:py-5 lg:px-7 lg:py-7 flex items-center justify-center hover:bg-[#DEDDDD] transition-colors">
                  <div className="flex items-center justify-center gap-2 w-full">
                    <span className="text-base sm:text-lg lg:text-[22px] font-medium text-[#2F1612] font-[Inter] leading-[1.3181818181818181] text-left flex-1">
                      {category.proTitle}
                    </span>
                    <FaArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-[#2F1612] flex-shrink-0" />
                  </div>
                </div>
              </RouterLink>
            ))}
            {otherCategory && (
              <RouterLink
                key={otherCategory.id}
                to={`${match.path}/hire/new?category=${otherCategory.id}`}
                onClick={() => {
                  logEvent("hero_section_cta_category_clicked", {
                    category: otherCategory.name,
                  });
                  logEvent("homeowner_hire_workflow_started", {
                    category: otherCategory.name,
                  });
                }}
                className="group"
              >
                <div className="w-full h-[65px] sm:h-[75px] lg:h-[85px] bg-[#E8E7E7] px-4 py-4 sm:px-6 sm:py-5 lg:px-7 lg:py-7 flex items-center justify-center hover:bg-[#DEDDDD] transition-colors">
                  <div className="flex items-center justify-center gap-2 w-full">
                    <span className="text-base sm:text-lg lg:text-[22px] font-medium text-[#2F1612] font-[Inter] leading-[1.3181818181818181] text-left flex-1">
                      {otherCategory.proTitle}
                    </span>
                    <FaArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-[#2F1612] flex-shrink-0" />
                  </div>
                </div>
              </RouterLink>
            )}
          </div>
        )}
      </PageSection>

      {/* Fifth Section */}
      <div
        className="w-full bg-[#FEFEFE] py-12 md:py-16"
        style={{
          borderTop: "0.5px solid #121212",
          borderBottom: "0.5px solid #121212",
        }}
      >
        {/* Project Outsource */}
        <PageSection>
          <ProjectOutsource
            onCtaClick={handleStartProjectFromService}
            userType="homeowner"
          />
        </PageSection>
      </div>

      {/* Fifth Section - Why Homeowners love LaborHack */}
      <PageSection className="bg-[#FDF1CC] border-b border-prussianBlue-300">
        <div className="flex flex-col gap-6 md:gap-8 py-16">
          <h2 className="text-2xl md:text-[32px] font-medium text-[#212121] leading-[1.25]">
            Why Homeowners love LaborHack
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 border-t border-prussianBlue-300 pt-12">
            {/* Item 1 */}
            <div className="flex flex-col gap-3 md:gap-6">
              {/* Icon */}
              <div className="w-full flex justify-start">
                <img
                  src={ChecklistIcon}
                  alt="Quality delivery"
                  className="h-16 lg:h-20"
                />
              </div>
              {/* Text */}
              <div className="flex flex-col gap-2">
                <h3 className="text-lg md:text-[22px] font-medium text-[#212121] leading-[1.318]">
                  Quality delivery
                </h3>
                <p className="text-sm md:text-base text-[#464646] leading-[1.875] lg:max-w-[304px]">
                  We manage and deliver your projects, from onboarding to expert
                  supervision and quality control.
                </p>
              </div>
            </div>

            {/* Item 2 */}
            <div className="flex flex-col gap-3 md:gap-6">
              <div className="w-full flex justify-start">
                <img
                  src={EvidenceIcon}
                  alt="Quality delivery"
                  className="h-16 lg:h-20"
                />
              </div>
              <div className="flex flex-col gap-2">
                <h3 className="text-lg md:text-[22px] font-medium text-[#212121] leading-[1.318]">
                  Proof of quality
                </h3>
                <p className="text-sm md:text-base text-[#464646] leading-[1.875] lg:max-w-[304px]">
                  {`Check any pro's work samples, client reviews, and identity
                    verification all in one place.`}
                </p>
              </div>
            </div>

            {/* Item 3 */}
            <div className="flex flex-col gap-3 md:gap-6">
              <div className="w-full flex justify-start">
                <img
                  src={NairaNoteIcon}
                  alt="Transparent pricing"
                  className="h-16 lg:h-20"
                />
              </div>
              <div className="flex flex-col gap-2">
                <h3 className="text-lg md:text-[22px] font-medium text-[#212121] leading-[1.318]">
                  Transparent pricing
                </h3>
                <p className="text-sm md:text-base text-[#464646] leading-[1.875] lg:max-w-[304px]">
                  {`No hidden costs or surprises. You'll receive upfront
                    pricing, so you know exactly what to expect.`}
                </p>
              </div>
            </div>

            {/* Item 4 */}
            <div className="flex flex-col gap-3 md:gap-6">
              <div className="w-full flex justify-start">
                <img
                  src={SafePaymentsIcon}
                  alt="Safe, easy payments"
                  className="h-16 lg:h-20"
                />
              </div>
              <div className="flex flex-col gap-2">
                <h3 className="text-lg md:text-[22px] font-medium text-[#212121] leading-[1.318]">
                  Safe, easy payments
                </h3>
                <p className="text-sm md:text-base text-[#464646] leading-[1.875] lg:max-w-[304px]">
                  Get an extra level of security with LaborHack Guarantee and
                  Payment Protection.
                </p>
              </div>
            </div>
          </div>
        </div>
      </PageSection>

      <ClientsFAQs
        items={homeownerFAQs}
        onReadAllFAQs={() => navigate("/faqs?tab=homeowners")}
      />

      <PageSection className="bg-white ">
        <div className="w-full my-16">
          <div className="border-y border-prussianBlue-300 grid grid-cols-1 md:grid-cols-3 py-16 gap-8">
            <div className="col-span-1">
              <h2 className="text-2xl md:text-4xl font-medium text-gray-900 mb-4 text-center md:text-left">
                Delivering real results
                <br className="hidden md:block" /> for homeowners
              </h2>
            </div>
            <div className="col-span-2 relative overflow-hidden hidden md:block">
              <Marquee autoFill={true} speed={150}>
                {stats.map((story, index) => (
                  <div
                    key={`${story.title}-${index}`}
                    className="flex items-center gap-4 min-w-[360px]  md:min-w-[480px] flex-shrink-0"
                  >
                    <img
                      src={story.icon}
                      alt={story.title}
                      className="w-14 h-14"
                    />
                    <p className="text-lg md:text-2xl text-[#212121] font-[Inter] leading-[1.2916667] max-w-96">
                      {story.title}
                    </p>
                  </div>
                ))}
              </Marquee>
            </div>

            <div className="col-span-1 md:hidden px-5">
              <div className="flex flex-col gap-4">
                {stats.map((story, index) => (
                  <div
                    key={`${story.title}-${index}`}
                    className="flex items-center gap-4"
                  >
                    <img
                      src={story.icon}
                      alt={story.title}
                      className="w-14 h-14"
                    />
                    <p className="text-lg md:text-2xl text-[#212121] font-[Inter] leading-[1.2916667] max-w-96">
                      {story.title}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </PageSection>

      {/* Footer */}
      <FooterV2
        ctaTitle="Ready to start your home project?"
        primaryCtaLabel="Get started"
        onPrimaryCta={handleCtaClick}
      />
    </div>
  );
};

export default HomeOwnersV2;
